import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, firstValueFrom, of } from 'rxjs';
import { environment } from '@env';
import { IResolvedUserSettings, IUserSettings } from '@common/microservice-types/db/settings.user';
import { IAnswerLength } from '@common/microservice-types/db/ai-models/answer-length';
import { IMessageHistoryPreset } from '@common/microservice-types/db/ai-models/message-history-preset';
import { ILocale } from '@common/microservice-types/db/locale';
import type { TInstructionKey } from '@common/microservice-types/db/instruction';
import type { TClientEnvironment } from '@common/constants/client-environments';

export type IUpdateUserSettingsDto = Partial<Pick<IUserSettings,
    'intelligencePreset' | 'selectedChatFamilyId' | 'answerLengthId' | 'messageHistoryId'
    | 'selectedChatModelId' | 'selectedImageModelId' | 'selectedSttModelId'
    | 'webSearchEnabled' | 'documentContextEnabled' | 'documentChatEnabled'>>
    & { locale?: string };

export function officeEnvironmentForHost(host: Office.HostType | string): TClientEnvironment
{
    switch (String(host).toLowerCase())
    {
        case 'word': return 'office-word';
        case 'excel': return 'office-excel';
        case 'powerpoint': return 'office-powerpoint';
        case 'outlook': return 'office-outlook';
        case 'onenote': return 'office-onenote';
        default: throw new Error(`Unsupported Office host: ${host}`);
    }
}

@Injectable({ providedIn: 'root' })
export class UserSettingsApiService
{
    private readonly baseUrl = `${ environment.office_api_host }/api/r2-chat/user/settings`;
    private readonly answerLengthsUrl = `${ environment.office_api_host }/api/r2-chat/answer-lengths`;
    private readonly messageHistoryPresetsUrl = `${ environment.office_api_host }/api/r2-chat/message-history-presets`;
    private readonly localesUrl = `${ environment.office_api_host }/api/r2-chat/locales`;

    private _answerLengths: IAnswerLength[] | null = null;
    private _messageHistoryPresets: IMessageHistoryPreset[] | null = null;
    private _locales: ILocale[] | null = null;
    private _settings: IResolvedUserSettings | null = null;
    private readonly _settingsChanges = new BehaviorSubject<IResolvedUserSettings | null>(null);
    public readonly settings$ = this._settingsChanges.asObservable();
    private _settingsLoad: { generation: number; sequence: number; promise: Promise<IResolvedUserSettings | null> } | null = null;
    private _patchSeq = 0;
    private _patchQueue: Promise<void> = Promise.resolve();
    private _failedPatch = false;
    private _generation = 0;
    private _loadSeq = 0;
    private _instructionRevision = 0;
    private _instructionSnapshot: Pick<IResolvedUserSettings,
        'instruction' | 'instructionKey' | 'customInstruction' | 'resolvedInstruction'> | null = null;
    private _activeEnvironment: TClientEnvironment | null = null;
    public get generation(): number { return this._generation; }
    public environmentId(): TClientEnvironment { return this.syncEnvironment(); }
    public get confirmedLocale(): string | null { return this._settings?.locale?.isoCode ?? null; }

    constructor(private readonly http: HttpClient) { }

    public async getSettings(force = false): Promise<IResolvedUserSettings | null>
    {
        const clientEnvironment = this.syncEnvironment();
        const generation = this._generation;
        const sequence = this._patchSeq;
        if (this._settingsLoad?.generation === generation && this._settingsLoad.sequence === sequence)
            return this._settingsLoad.promise;
        if (!force && this._settings !== null) { return this._settings; }

        const load = { generation, sequence, promise: this.loadSettings(generation, sequence, ++this._loadSeq, clientEnvironment) };
        this._settingsLoad = load;
        try { return await load.promise; }
        finally { if (this._settingsLoad === load) this._settingsLoad = null; }
    }

    private async loadSettings(generation: number, sequence: number, loadSeq: number, clientEnvironment: TClientEnvironment): Promise<IResolvedUserSettings | null>
    {
        const instructionRevision = this._instructionRevision;
        try
        {
            const settings = await firstValueFrom(
                this.http.get<IResolvedUserSettings>(`${this.baseUrl}/by-environment`, { params: { environment: clientEnvironment } }),
            );
            if (generation !== this._generation || clientEnvironment !== this.currentEnvironment()) return null;
            if (sequence !== this._patchSeq) return this._settings;
            if (loadSeq !== this._loadSeq) return this._settingsLoad?.promise ?? this._settings;
            this._settings = this.preserveInstruction(settings, instructionRevision);
            this._settingsChanges.next(this._settings);
            return this._settings;
        }
        catch
        {
            return null;
        }
    }

    /** @deprecated Use getSettings() for cached access. */
    public load(): Observable<IResolvedUserSettings | null>
    {
        const clientEnvironment = this.syncEnvironment();
        return this.http.get<IResolvedUserSettings>(`${this.baseUrl}/by-environment`, { params: { environment: clientEnvironment } }).pipe(
            catchError(() => of(null)),
        );
    }

    public async patch(dto: IUpdateUserSettingsDto): Promise<IResolvedUserSettings | null>
    {
        const clientEnvironment = this.syncEnvironment();
        const generation = this._generation;
        const instructionRevision = this._instructionRevision;
        ++this._patchSeq;
        const work = this._patchQueue.then(async () =>
        {
            if (generation !== this._generation || clientEnvironment !== this.currentEnvironment()) return null;
            try
            {
                const updated = await firstValueFrom(this.http.patch<IResolvedUserSettings>(
                    `${this.baseUrl}/by-environment`, dto, { params: { environment: clientEnvironment } }));
                if (generation !== this._generation || clientEnvironment !== this.currentEnvironment()) return null;
                this._settings = this.preserveInstruction(updated, instructionRevision);
                this._settingsChanges.next(this._settings);
                this._failedPatch = false;
                return updated;
            }
            catch
            {
                if (generation === this._generation) this._failedPatch = true;
                return null;
            }
        });
        this._patchQueue = work.then(() => undefined);
        return work;
    }

    public async waitForConfirmedSettings(): Promise<IResolvedUserSettings>
    {
        const clientEnvironment = this.syncEnvironment();
        const generation = this._generation;
        let pending: Promise<void>;
        let loading: Promise<IResolvedUserSettings | null> | null;
        do
        {
            pending = this._patchQueue;
            loading = this._settingsLoad?.promise ?? null;
            await pending;
            if (loading) await loading;
        } while ((pending !== this._patchQueue || this._settingsLoad !== null) && generation === this._generation);
        if (generation !== this._generation || clientEnvironment !== this.currentEnvironment() || this._failedPatch)
            throw new Error('Account settings could not be saved. Retry the failed choice.');
        const settings = await this.getSettings();
        if (!settings || generation !== this._generation || clientEnvironment !== this.currentEnvironment())
            throw new Error('Account settings are unavailable. Retry the send.');
        return settings;
    }

    public async getHistoryEnabled(force = false): Promise<boolean | null>
    {
        const settings = await this.getSettings(force);
        return settings ? settings.messageHistoryId !== null : null;
    }

    public async setHistoryEnabled(enabled: boolean): Promise<boolean>
    {
        this.syncEnvironment();
        const generation = this._generation;
        const settings = await this.getSettings();
        if (!settings || generation !== this._generation) return false;

        const messageHistoryId = enabled
            ? settings.messageHistoryId
                ?? (await this.getMessageHistoryPresets()).find(preset => preset.messageHistoryPresetId === 'MEDIUM')?.id
                ?? (await this.getMessageHistoryPresets())[ 0 ]?.id
                ?? null
            : null;
        if (generation !== this._generation || (enabled && messageHistoryId === null)) return false;

        const updated = await this.patch({ messageHistoryId });
        return !!updated && (updated.messageHistoryId !== null) === enabled;
    }

    public async getAnswerLengths(): Promise<IAnswerLength[]>
    {
        if (this._answerLengths !== null) { return this._answerLengths; }
        try
        {
            this._answerLengths = await firstValueFrom(
                this.http.get<IAnswerLength[]>(this.answerLengthsUrl),
            );
            return this._answerLengths;
        }
        catch
        {
            return [];
        }
    }

    public async getMessageHistoryPresets(): Promise<IMessageHistoryPreset[]>
    {
        if (this._messageHistoryPresets !== null) { return this._messageHistoryPresets; }
        try
        {
            this._messageHistoryPresets = await firstValueFrom(
                this.http.get<IMessageHistoryPreset[]>(this.messageHistoryPresetsUrl),
            );
            return this._messageHistoryPresets;
        }
        catch
        {
            return [];
        }
    }

    public async getLocales(): Promise<ILocale[]>
    {
        if (this._locales !== null) { return this._locales; }
        try
        {
            this._locales = await firstValueFrom(
                this.http.get<ILocale[]>(this.localesUrl),
            );
            return this._locales;
        }
        catch
        {
            return [];
        }
    }

    /** Persists the chosen locale. Backend resolves the ISO code to localeId. */
    public async setLocale(locale: ILocale): Promise<IResolvedUserSettings>
    {
        const updated = await this.patch({ locale: locale.isoCode });
        if (!updated || !this._settings) throw new Error('Locale could not be saved');
        return this._settings;
    }

    public async selectInstruction(instructionKey: TInstructionKey): Promise<IResolvedUserSettings>
    {
        this.syncEnvironment();
        const generation = this._generation;
        const work = this._patchQueue.then(async () =>
        {
            if (generation !== this._generation) throw new Error('Instruction context changed');
            try
            {
                const updated = await firstValueFrom(this.http.patch<IResolvedUserSettings>(this.baseUrl, { instructionKey }));
                if (generation !== this._generation) throw new Error('Instruction context changed');
                this.acceptInstructionSettings(updated, generation);
                this._failedPatch = false;
                return updated;
            }
            catch (error)
            {
                if (generation === this._generation) this._failedPatch = true;
                throw error;
            }
        });
        this._patchQueue = work.then(() => undefined, () => undefined);
        return work;
    }

    public acceptInstructionSettings(updated: IResolvedUserSettings, generation = this._generation): void
    {
        if (generation !== this._generation) return;
        this._instructionRevision++;
        this._instructionSnapshot = {
            instructionKey: updated.instructionKey,
            instruction: updated.instruction, customInstruction: updated.customInstruction,
            resolvedInstruction: updated.resolvedInstruction,
        };
        this._settings = this._settings ? { ...this._settings, ...this._instructionSnapshot } : null;
        this._settingsChanges.next(this._settings);
    }

    public reset(): void
    {
        this._generation++;
        this._patchSeq++;
        this._settings = null;
        this._instructionSnapshot = null;
        this._failedPatch = false;
        this._settingsLoad = null;
        this._patchQueue = Promise.resolve();
        this._settingsChanges.next(null);
    }

    private preserveInstruction(updated: IResolvedUserSettings, revision: number): IResolvedUserSettings
    {
        if (!this._instructionSnapshot || revision === this._instructionRevision) return updated;
        return { ...updated, ...this._instructionSnapshot };
    }

    private currentEnvironment(): TClientEnvironment
    {
        if (typeof Office === 'undefined' || !Office.context?.host)
            throw new Error('Office host is unavailable');
        return officeEnvironmentForHost(Office.context.host);
    }

    private syncEnvironment(): TClientEnvironment
    {
        const clientEnvironment = this.currentEnvironment();
        if (this._activeEnvironment && this._activeEnvironment !== clientEnvironment) this.reset();
        this._activeEnvironment = clientEnvironment;
        return clientEnvironment;
    }
}
